import { z } from "zod";

import { delay } from "../../AsyncUtils";
import { iterateValues } from "../../IterableUtils";
import { NotImplementedError } from "../../Errors";
import { reject } from "../../TypingUtils";
import {
    INidusClient,
    NidusEntry,
    NidusEntryData,
    NidusEntryId,
    NidusName,
    NidusReferenceKey,
    NidusSearchResponse,
} from "../INidusClient";

import { createUniqueNidusEntryId } from "./CreateUniqueNidusEntryId";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const nidusEntryIdSchema: z.Schema<NidusEntryId> = z.string() as any;

const LocalStorageNidusDataSchema = z.object({
    data: z.array(
        z.object({
            id: nidusEntryIdSchema,
            data: z.record(z.string()),
        })
    ),
});

type LocalStorageNidusData = z.infer<typeof LocalStorageNidusDataSchema>;

export class LocalStorageBasedNidusClient implements INidusClient {
    private async fakeDelay(): Promise<void> {
        await delay(400);
    }

    private getNidusData(nidusName: NidusName, key: NidusReferenceKey): undefined | LocalStorageNidusData {
        const rawResult = parseJsonOrUndefined(localStorage.getItem(`Candy:LocalNidusData:${nidusName}-${key}`));
        if (rawResult == undefined) {
            return undefined;
        }
        return LocalStorageNidusDataSchema.parse(rawResult);
    }

    private updateNidusData(
        nidusName: NidusName,
        key: NidusReferenceKey,
        updateFn: (prevValue: LocalStorageNidusData) => LocalStorageNidusData
    ): void {
        const prevValueRaw = parseJsonOrUndefined(localStorage.getItem(`Candy:LocalNidusData:${nidusName}-${key}`));
        const prevValue = prevValueRaw == undefined ? undefined : LocalStorageNidusDataSchema.parse(prevValueRaw);
        const nextValue = updateFn(prevValue ?? { data: [] });
        localStorage.setItem(`Candy:LocalNidusData:${nidusName}-${key}`, JSON.stringify(nextValue));
    }

    public async search(
        nidusName: NidusName,
        key: NidusReferenceKey,
        searchValue: string | null | undefined
    ): Promise<NidusSearchResponse> {
        await this.fakeDelay();
        const nidusData = this.getNidusData(nidusName, key);
        if (nidusData == undefined) {
            return { items: [], totalSuitableCount: 0 };
        }
        if (searchValue == "" || searchValue == undefined) {
            const allMatchedItems = nidusData.data;
            return {
                items: allMatchedItems.slice(0, 10),
                totalSuitableCount: allMatchedItems.length,
            };
        }
        const normalizedSearchValue = searchValue.toLowerCase();
        const isEntryFieldValueMatch = (value: string) => value.toLowerCase().includes(normalizedSearchValue ?? "");
        const isEntryMatch = (entry: NidusEntry) =>
            Iterator.from(iterateValues(entry.data)).some(isEntryFieldValueMatch);
        const allMatchedItems = nidusData.data.filter(isEntryMatch);
        return {
            items: allMatchedItems.slice(0, 10),
            totalSuitableCount: allMatchedItems.length,
        };
    }

    public searchMinitour(
        nidusName: string,
        key: NidusReferenceKey,
        searchValue: string | null | undefined
    ): Promise<NidusSearchResponse> {
        return this.search(nidusName, key, searchValue);
    }

    public async insert(nidusName: NidusName, key: NidusReferenceKey, entryData: NidusEntryData): Promise<NidusEntry> {
        await this.fakeDelay();
        const newObject: NidusEntry = { data: entryData, id: createUniqueNidusEntryId() };
        this.updateNidusData(nidusName, key, prevNidusData => {
            prevNidusData.data.push(newObject);
            return prevNidusData;
        });
        return newObject;
    }

    public async delete(nidusName: NidusName, key: NidusReferenceKey, objectId: NidusEntryId): Promise<void> {
        await this.fakeDelay();
        this.updateNidusData(nidusName, key, prevNidusData => {
            prevNidusData.data = prevNidusData.data.filter(x => x.id !== objectId);
            return prevNidusData;
        });
    }

    public async update(
        nidusName: NidusName,
        key: NidusReferenceKey,
        objectId: NidusEntryId,
        obj: NidusEntryData
    ): Promise<void> {
        await this.fakeDelay();
        this.updateNidusData(nidusName, key, prevNidusData => {
            const itemToUpdate =
                prevNidusData.data.find(x => x.id === objectId) ?? reject(`Item with id ${objectId} not found`);
            itemToUpdate.data = obj;
            return prevNidusData;
        });
    }

    public async batchInsert(): Promise<void> {
        throw new NotImplementedError();
    }
}

function parseJsonOrUndefined(input: undefined | null | string): undefined | unknown {
    if (input == undefined) {
        return undefined;
    }
    try {
        return JSON.parse(input);
    } catch {
        return undefined;
    }
}
